import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Search } from "lucide-react";
import { Link } from "react-router-dom";
import Loading from "../components/Loading";
import NewsCard from "../components/NewsCard";
import LaunchCard from "../components/LaunchCard";
import RocketCard from "../components/RocketCard";

const TABS = [
  { k: "all", label: "ALL" },
  { k: "news", label: "NEWS" },
  { k: "launch", label: "LAUNCH" },
  { k: "rocket", label: "ROCKET" },
];

export default function ScanPage() {
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [results, setResults] = useState({ news: null, launches: null, rockets: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const query = q.trim();
    if (!query || query.length < 2) { setResults({ news: null, launches: null, rockets: null }); return; }
    setLoading(true);
    const timer = setTimeout(() => {
      api.get("/search", { params: { q: query, type } })
        .then(r => setResults({ news: r.data.news, launches: r.data.launches, rockets: r.data.rockets }))
        .catch(() => setResults({ news: null, launches: null, rockets: null }))
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [q, type]);

  const news = results.news?.results || [];
  const launches = results.launches?.results || [];
  const rockets = results.rockets?.results || [];
  const show = (k) => type === "all" || type === k;

  return (
    <div data-testid="page-scan">
      <div className="mb-6 border-b border-[color:var(--sat-border)] pb-4">
        <div className="sat-label sat-cyan">CHANNEL // 005</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-1">SCAN THE COSMOS</h1>
        <p className="text-sm text-[color:var(--sat-text-dim)] mt-2">Deep-radar sweep across news, missions, and vehicle registry.</p>
      </div>

      <div className="relative mb-4">
        <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-[color:var(--sat-cyan)]" />
        <input type="search" autoFocus placeholder="TRANSMIT QUERY…" value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="scan-input"
          className="!pl-14 !py-4 !text-base" />
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto">
        {TABS.map(t => (
          <button key={t.k} data-testid={`scan-tab-${t.k}`} onClick={() => setType(t.k)}
            className={`sat-btn flex-shrink-0 ${type === t.k ? "sat-btn-solid" : ""}`}>{t.label}</button>
        ))}
      </div>

      {loading && <Loading message="SCANNING" />}

      {!loading && q && (
        <div className="space-y-8">
          {show("news") && news.length > 0 && (
            <section>
              <div className="sat-label sat-cyan mb-3">NEWS · {news.length}</div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">{news.map(a => <NewsCard key={a.id} article={a} />)}</div>
            </section>
          )}
          {show("launch") && launches.length > 0 && (
            <section>
              <div className="sat-label sat-cyan mb-3">LAUNCHES · {launches.length}</div>
              <div className="flex flex-col gap-3">{launches.map(l => (
                <Link key={l.id} to={`/launches/${l.id}`} className="sat-surface sat-brackets p-4 group flex items-center gap-4 hover:sat-box-glow" data-testid={`scan-launch-${l.id}`}>
                  <div className="w-16 h-16 bg-black flex-shrink-0">{l.image && <img src={l.image} alt="" className="w-full h-full object-cover"/>}</div>
                  <div className="flex-1">
                    <div className="font-display font-bold group-hover:text-[color:var(--sat-cyan)] transition-colors">{l.name}</div>
                    <div className="sat-label mt-1">{l.status?.name}</div>
                  </div>
                </Link>
              ))}</div>
            </section>
          )}
          {show("rocket") && rockets.length > 0 && (
            <section>
              <div className="sat-label sat-cyan mb-3">ROCKETS · {rockets.length}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{rockets.map(r => <RocketCard key={r.id} rocket={r} />)}</div>
            </section>
          )}
          {news.length === 0 && launches.length === 0 && rockets.length === 0 && (
            <div className="text-center py-16 sat-label">NO SIGNALS DETECTED</div>
          )}
        </div>
      )}

      {!q && (
        <div className="text-center py-20 sat-label">AWAITING QUERY INPUT…</div>
      )}
    </div>
  );
}
